import { SoSelectObjectItem } from "@/types/components/SoSelect"
import { SoRadioGroupItem } from "@/types/components/SoRadioGroup"

export interface JobType {
  name: string
  params: {
    appointment?: string
    install?: string
    meter?: string
    notes?: string
  }
}

export const IMBP_TEXT_AREA_MAX_LENGTH = 120

export const IMBP_APPOINTMENT_CANCELLATION_HOURS = 24

export enum AppointmentType {
  NEW_CONNECTION = "NEW_CONNECTION",
  METER_EXCHANGE = "METER_EXCHANGE",
  WARRANT = "WARRANT",
  COMMISSION_JOIN = "COMMISSION_JOIN",
  FAULTS = "FAULTS",
  EMERGENCY_FAULTS = "EMERGENCY_FAULTS",
  EMERGENCY_METER_EXCHANGE = "EMERGENCY_METER_EXCHANGE",
  WARRANT_METER_EXCHANGE = "WARRANT_METER_EXCHANGE",
  METER_REMOVAL = "METER_REMOVAL",
  WARRANT_REMOVAL = "WARRANT_REMOVAL",
  INVESTIGATION = "INVESTIGATION",
  EMERGENCY_INVESTIGATION = "EMERGENCY_INVESTIGATION",
}

export const meterTypesAES: SoRadioGroupItem[] = [
  { text: "Electricity", value: "ELEC_ONLY" },
  { text: "Gas", value: "GAS_ONLY" },
  { text: "Dual", value: "DUAL" },
]

export const installationTypesAES: SoSelectObjectItem[] = [
  { title: "Traditional", value: "TRADITIONAL" },
  { title: "SMETS1", value: "SMETS1" },
  { title: "SMETS2", value: "SMETS2" },
]

export const appointmentTypesAES: SoSelectObjectItem[] = [
  { title: "New Connection", value: AppointmentType.NEW_CONNECTION },
  { title: "Meter Exchange", value: AppointmentType.METER_EXCHANGE },
  { title: "Warrant", value: AppointmentType.WARRANT },
  { title: "Commission/Join", value: AppointmentType.COMMISSION_JOIN },
  { title: "Faults", value: AppointmentType.FAULTS },
  { title: "Emergency Fault", value: AppointmentType.EMERGENCY_FAULTS },
  {
    title: "Emergency Meter Exchange",
    value: AppointmentType.EMERGENCY_METER_EXCHANGE,
  },
  {
    title: "Warrant Meter Exchange",
    value: AppointmentType.WARRANT_METER_EXCHANGE,
  },
  { title: "Meter Removal", value: AppointmentType.METER_REMOVAL },
  { title: "Warrant Removal", value: AppointmentType.WARRANT_REMOVAL },
  { title: "Investigation", value: AppointmentType.INVESTIGATION },
  {
    title: "Emergency Investigation",
    value: AppointmentType.EMERGENCY_INVESTIGATION,
  },
]

export const jobTypes: JobType[] = [
  {
    name: "smart-install",
    params: {
      appointment: "METER_EXCHANGE",
      install: "SMETS2",
    },
  },
  {
    name: "meter-removal",
    params: {
      appointment: "METER_REMOVAL",
    },
  },
  {
    name: "meter-tails",
    params: {
      meter: "ELEC_ONLY",
      appointment: "INVESTIGATION",
      notes: "This is a meter tails job",
    },
  },
  {
    name: "isolation-switch",
    params: {
      meter: "ELEC_ONLY",
      appointment: "INVESTIGATION",
      notes: "This is an isolation switch job",
    },
  },
  {
    name: "meter-relocation",
    params: {
      appointment: "INVESTIGATION",
      notes: "This is a meter relocation job",
    },
  },
  {
    name: "meter-relocation-1m",
    params: {
      appointment: "INVESTIGATION",
      notes:
        "This is a meter relocation job more than 1m - (G)DNO are notified",
    },
  },
]

export const meterAppointmentTypeValues = {
  GAS_ONLY: "GAS_ONLY",
  ELEC_ONLY: "ELEC_ONLY",
  DUAL: "DUAL",
} as const

export type MeterAppointmentType =
  (typeof meterAppointmentTypeValues)[keyof typeof meterAppointmentTypeValues]

export const SMEX_JOB_NOTES: Record<string, string> = {
  "DUAL_TRAD-TRAD": "Upgrade both trad meters on site to SMETS2.",
  "DUAL_SMETS1-TRAD":
    "Upgrade both the S1 meter and trad meter on site to SMETS2.",
  "DUAL_SMETS1-SMETS1": "Upgrade both S1 meters on site to SMETS2.",
  "DUAL_SMETS1-SMETS2":
    "Replace the non-communicating S2 meter AND upgrade the S1 meter to SMETS2.",
  "DUAL_SMETS2-TRAD":
    "Replace the non-communicating S2 meter AND upgrade the TRAD meter to SMETS2.",
  "DUAL_SMETS2-SMETS2": "Replace both non-communicating S2 meters on site.",
  DUAL_GAS_ONLY: "Upgrade the GAS meter on site to SMETS2.",
  DUAL_GAS_ONLY_SMETS2: "Replace the non-communicating S2 GAS meter.",
  DUAL_ELEC_ONLY: "Upgrade the ELEC meter on site to SMETS2.",
  DUAL_ELEC_ONLY_SMETS2: "Replace the non-communicating S2 ELEC meter.",
  SINGLE_GAS:
    "Gas only site. Hot shoe required. Upgrade the meter on site to SMETS2.",
  SINGLE_GAS_SMETS2:
    "Gas only site. Hot shoe required. Replace the non-communicating S2 meter.",
  SINGLE_ELEC: "Elec only site. Upgrade the meter on site to SMETS2.",
  SINGLE_ELEC_SMETS2: "Elec only site. Replace the non-communicating S2 meter.",
}

export const METER_TYPE = {
  TRAD: "TRAD",
  SMETS1: "SMETS1",
  SMETS2: "SMETS2",
  UNKNOWN: "UNKNOWN",
} as const

export type MeterType = (typeof METER_TYPE)[keyof typeof METER_TYPE]
